import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, ExternalLink, GripVertical, Eye, EyeOff, Settings2 } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFeaturedOn = () => {
  const { token } = useAuth();
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", logo: "", url: "", display_order: 0, is_visible: true });
  const [carouselSettings, setCarouselSettings] = useState({
    autoplay: true, autoplay_speed: 3000, loop: true,
    logos_desktop: 6, logos_tablet: 4, logos_mobile: 2,
  });
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    fetchPubs();
    fetchSettings();
  }, []);

  const fetchPubs = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/featured-publications?visible_only=false`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPublications(res.data);
    } catch { toast.error("Failed to load publications"); }
    finally { setLoading(false); }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/carousel-settings`);
      setCarouselSettings(res.data);
    } catch (err) { console.error("Settings fetch error:", err); }
  };

  const resetForm = () => {
    setForm({ name: "", logo: "", url: "", display_order: publications.length, is_visible: true });
    setEditing(null);
  };

  const handleEdit = (pub) => {
    setEditing(pub);
    setForm({
      name: pub.name, logo: pub.logo || "", url: pub.url,
      display_order: pub.display_order, is_visible: pub.is_visible,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.url.trim()) { toast.error("Name and URL are required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/featured-publications/${editing.id}`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Publication updated");
      } else {
        await axios.post(`${BACKEND_URL}/api/featured-publications`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Publication added");
      }
      setShowModal(false);
      resetForm();
      fetchPubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this publication?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/featured-publications/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Publication deleted");
      fetchPubs();
    } catch { toast.error("Failed to delete"); }
  };

  const toggleVisibility = async (pub) => {
    try {
      await axios.put(`${BACKEND_URL}/api/featured-publications/${pub.id}`, {
        is_visible: !pub.is_visible,
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchPubs();
    } catch { toast.error("Failed to update visibility"); }
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/carousel-settings`, carouselSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Carousel settings saved");
      setShowSettings(false);
    } catch { toast.error("Failed to save settings"); }
  };

  // Drag & Drop reorder
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = useCallback(async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const updated = [...publications];
    const [moved] = updated.splice(dragIdx, 1);
    updated.splice(targetIdx, 0, moved);
    setPublications(updated);
    setDragIdx(null);
    try {
      await axios.put(`${BACKEND_URL}/api/featured-publications/reorder`,
        updated.map((p) => p.id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Order updated");
    } catch { toast.error("Failed to save order"); fetchPubs(); }
  }, [dragIdx, publications, token]);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-featured-on">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground mb-2">Featured On</h1>
            <p className="text-muted-foreground">Manage publications & carousel settings</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowSettings(true)} className="rounded-full" data-testid="carousel-settings-btn">
              <Settings2 className="w-4 h-4 mr-2" />Settings
            </Button>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="rounded-full" data-testid="add-publication-btn">
              <Plus className="w-4 h-4 mr-2" />Add Publication
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : publications.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left p-4 font-medium text-muted-foreground w-10"></th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Logo</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Publication</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">URL</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Visible</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {publications.map((pub, idx) => (
                  <tr
                    key={pub.id}
                    className={`border-b last:border-0 hover:bg-muted/20 ${dragIdx === idx ? "opacity-50" : ""}`}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(idx)}
                    data-testid={`pub-row-${pub.id}`}
                  >
                    <td className="p-4 cursor-grab active:cursor-grabbing">
                      <GripVertical className="w-4 h-4 text-muted-foreground" />
                    </td>
                    <td className="p-4">
                      {pub.logo ? (
                        <img src={pub.logo} alt={pub.name} className="h-8 w-auto max-w-[120px] object-contain" />
                      ) : (
                        <div className="h-8 w-16 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">No logo</div>
                      )}
                    </td>
                    <td className="p-4 font-medium">{pub.name}</td>
                    <td className="p-4">
                      <a href={pub.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1 max-w-[250px] truncate">
                        {pub.url.replace(/^https?:\/\//, "").slice(0, 35)}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    </td>
                    <td className="p-4">
                      <button onClick={() => toggleVisibility(pub)} className="p-1 rounded hover:bg-muted" data-testid={`toggle-vis-${pub.id}`}>
                        {pub.is_visible ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(pub)} data-testid={`edit-pub-${pub.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(pub.id)} className="text-red-500 hover:bg-red-50" data-testid={`delete-pub-${pub.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-muted-foreground px-4 py-2 bg-muted/10">Drag rows to reorder logos</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <ExternalLink className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Publications Yet</h3>
            <p className="text-muted-foreground mb-6">Add media publications where your brand has been featured.</p>
            <Button onClick={() => { resetForm(); setShowModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />Add First Publication
            </Button>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Publication" : "Add Publication"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="publication-form">
              <div>
                <label className="text-sm font-medium mb-1 block">Publication Name *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Lokmat Times" data-testid="form-pub-name" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Article URL *</label>
                <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="form-pub-url" />
              </div>
              <ImageUpload value={form.logo} onChange={(url) => setForm({ ...form, logo: url })} token={token} label="Publication Logo" />
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_visible} onChange={(e) => setForm({ ...form, is_visible: e.target.checked })} id="visible" className="rounded" data-testid="form-pub-visible" />
                <label htmlFor="visible" className="text-sm font-medium">Visible on homepage</label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="form-pub-submit">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editing ? "Update" : "Add"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Carousel Settings Dialog */}
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Carousel Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-5" data-testid="carousel-settings-form">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Autoplay</label>
                <Switch checked={carouselSettings.autoplay} onCheckedChange={(v) => setCarouselSettings({ ...carouselSettings, autoplay: v })} data-testid="settings-autoplay" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Autoplay Speed (ms)</label>
                <Input type="number" min={1000} max={10000} step={500} value={carouselSettings.autoplay_speed} onChange={(e) => setCarouselSettings({ ...carouselSettings, autoplay_speed: parseInt(e.target.value) || 3000 })} data-testid="settings-speed" />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Infinite Loop</label>
                <Switch checked={carouselSettings.loop} onCheckedChange={(v) => setCarouselSettings({ ...carouselSettings, loop: v })} data-testid="settings-loop" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block text-muted-foreground">Desktop</label>
                  <Input type="number" min={1} max={10} value={carouselSettings.logos_desktop} onChange={(e) => setCarouselSettings({ ...carouselSettings, logos_desktop: parseInt(e.target.value) || 6 })} data-testid="settings-desktop" />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block text-muted-foreground">Tablet</label>
                  <Input type="number" min={1} max={8} value={carouselSettings.logos_tablet} onChange={(e) => setCarouselSettings({ ...carouselSettings, logos_tablet: parseInt(e.target.value) || 4 })} data-testid="settings-tablet" />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block text-muted-foreground">Mobile</label>
                  <Input type="number" min={1} max={4} value={carouselSettings.logos_mobile} onChange={(e) => setCarouselSettings({ ...carouselSettings, logos_mobile: parseInt(e.target.value) || 2 })} data-testid="settings-mobile" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
                <Button onClick={saveSettings} data-testid="settings-save-btn">Save Settings</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminFeaturedOn;
